import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { MONTHS } from "@/lib/constants";
import { Check, Upload, Image as ImageIcon, FileText, X, Plus, FileDown, Loader2, CalendarClock, Ban, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function CalendarEntryDetail() {
  const { calendarId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = ["hr", "super_admin"].includes(user.system_role);
  const [entry, setEntry] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [training, setTraining] = useState(null);
  const [tab, setTab] = useState("nominees");
  const [search, setSearch] = useState("");
  const [showRevise, setShowRevise] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showConfirmNominations, setShowConfirmNominations] = useState(false);
  const [confirmingNoms, setConfirmingNoms] = useState(false);

  const load = async () => {
    const c = (await api.get(`/calendar/${calendarId}`)).data;
    setEntry(c);
    if (c.linked_training_id) {
      try {
        const t = (await api.get(`/trainings/${c.linked_training_id}`)).data;
        setTraining(t);
      } catch (e) {
        // Training may have been deleted or attendance not yet recorded.
        if (process.env.NODE_ENV !== "production") {
          console.debug("Linked training fetch failed (safe to ignore):", e?.message);
        }
      }
    }
  };

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setAllEmployees(r.data));
    api.get(`/calendar/${calendarId}/suggested-nominees`).then((r) => setSuggested(r.data));
  }, [calendarId]);

  if (!entry) return <div className="p-10 text-sm text-[#666666]">Loading…</div>;

  const nomSet = new Set(entry.nominated_participants);
  const empById = Object.fromEntries(allEmployees.map((e) => [e.employee_id, e]));
  const conducted = entry.status === "conducted";

  const visibleTabs = useMemo(
    () => ["nominees", "conduct"].filter((t) => t !== "conduct" || canEdit || conducted),
    [canEdit, conducted]
  );
  const cancelled = entry.status === "cancelled";
  const canReviseOrCancel = canEdit && !conducted && !cancelled;
  // When nominations are confirmed, hide the nominate/remove toggles until the
  // user explicitly clicks "Edit nominations" (which unconfirms).
  const nominationsConfirmed = !!entry.nominations_confirmed;
  const canEditNominees = canEdit && !conducted && !cancelled && !nominationsConfirmed;
  const confirmedNominees = entry.nominated_participants
    .map((id) => empById[id])
    .filter(Boolean)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const toggleNominee = async (id) => {
    if (conducted || cancelled) return;
    const next = new Set(nomSet);
    next.has(id) ? next.delete(id) : next.add(id);
    await api.patch(`/calendar/${calendarId}`, { nominated_participants: Array.from(next) });
    load();
  };

  const reviseMonth = async (newMonth) => {
    try {
      await api.patch(`/calendar/${calendarId}`, { target_month: newMonth });
      toast.success(`Revised to ${MONTHS[newMonth - 1]}`);
      setShowRevise(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revise failed");
    }
  };

  const cancelEntry = async () => {
    try {
      await api.post(`/calendar/${calendarId}/cancel`);
      toast.success("Entry cancelled. It is excluded from the Training Plan PDF.");
      setConfirmCancel(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const confirmNominations = async () => {
    setConfirmingNoms(true);
    try {
      await api.post(`/calendar/${calendarId}/confirm-nominations`);
      toast.success(`Training plan confirmed for ${entry.skill_name} — ${MONTHS[entry.target_month - 1]}`);
      setShowConfirmNominations(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirmation failed");
    } finally {
      setConfirmingNoms(false);
    }
  };

  const unconfirmNominations = async () => {
    try {
      await api.post(`/calendar/${calendarId}/unconfirm-nominations`);
      toast.info("Nominations re-opened for editing.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-open nominations");
    }
  };

  const filteredEmployees = allEmployees.filter((e) =>
    e.is_active && (
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      e.department.toLowerCase().includes(search.toLowerCase())
    )
  );

  return (
    <div data-testid="calendar-detail-page">
      <PageHeader
        eyebrow={`${MONTHS[entry.target_month - 1]} ${entry.year} · ${entry.source === "TNI_driven" ? "TNI-driven" : "Mandatory"}`}
        title={entry.skill_name}
        subtitle={entry.is_revised ? `Revised from ${MONTHS[(entry.original_month || 1) - 1]}` : ""}
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Calendar", to: "/calendar" },
          { label: String(entry.year) },
          { label: `${MONTHS[entry.target_month - 1]} — ${entry.skill_name}` },
        ]}
        action={
          <div className="flex items-center gap-2">
            {canReviseOrCancel && (
              <>
                <button
                  type="button"
                  onClick={() => setShowRevise(true)}
                  data-testid="revise-entry-button"
                  className="inline-flex items-center gap-1.5 text-sm border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm px-3 py-2"
                >
                  <CalendarClock size={14} /> Revise
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmCancel(true)}
                  data-testid="cancel-entry-button"
                  className="inline-flex items-center gap-1.5 text-sm border border-red-200 text-[#C41E1E] hover:bg-red-50 rounded-sm px-3 py-2"
                >
                  <Ban size={14} /> Cancel entry
                </button>
              </>
            )}
            <StatusPill status={entry.status} testid="entry-status" />
          </div>
        }
      />

      <div className="px-4 md:px-10 py-8">
        {cancelled && (
          <div
            data-testid="cancelled-banner"
            className="mb-6 border border-neutral-300 bg-neutral-100 text-neutral-700 rounded-sm px-4 py-3 text-sm flex items-start gap-2"
          >
            <Ban size={16} className="mt-0.5 shrink-0 text-neutral-500" />
            <div>
              <div className="font-semibold">This entry has been cancelled.</div>
              <div className="text-xs text-[#666666] mt-0.5">
                It remains visible in the calendar with a <span className="font-mono">CANCELLED</span> badge but
                is excluded from the Annual Training Plan PDF and from nominee auto-suggestions.
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-1 border-b border-[#E5E5E5] mb-6">
          {/* The "conduct" tab is only relevant to HR/Super Admin (to record a session)
             and to anyone viewing a session that has already been conducted (the read-
             only Training Record). Hide it entirely for Quality / Management / Employee
             when the training is not yet conducted. */}
          {visibleTabs.map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? "border-[#C41E1E] text-[#C41E1E]" : "border-transparent text-[#666666] hover:text-[#0D0D0D]"
                }`}
              >
                {t === "nominees" ? `Nominees (${entry.nominated_participants.length})` : conducted ? "Training Record" : "Conduct Training"}
              </button>
            ))}
        </div>

        {tab === "nominees" && (
          <div className="space-y-6">
            {/* Confirmed nominees — the single source-of-truth roster for the trainer. */}
            <div className="bc-card" data-testid="confirmed-nominees-section">
              <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Confirmed Nominees ({confirmedNominees.length})</h3>
                  <div className="text-[11px] text-[#666666] mt-0.5">
                    {nominationsConfirmed
                      ? "This roster is locked. Click 'Edit nominations' to make changes."
                      : "Active roster — keep nominating below, then confirm to lock it in."}
                  </div>
                </div>
                {nominationsConfirmed && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      data-testid="nominations-confirmed-badge"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-sm px-2.5 py-1"
                    >
                      <Check size={12} /> Nominations Confirmed
                    </span>
                    {canEdit && !conducted && !cancelled && (
                      <button
                        type="button"
                        onClick={unconfirmNominations}
                        data-testid="edit-nominations-link"
                        className="text-xs text-[#C41E1E] hover:underline"
                      >
                        Edit nominations
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="divide-y divide-[#E5E5E5]" data-testid="confirmed-nominees-list">
                {confirmedNominees.length === 0 ? (
                  <div className="px-5 py-8 text-sm text-[#666666] text-center italic">
                    No nominees yet. Use the Auto-suggested or All employees lists below to add people to this training.
                  </div>
                ) : (
                  confirmedNominees.map((e) => (
                    <div key={e.employee_id} className="px-5 py-2.5 flex items-center justify-between gap-3" data-testid={`confirmed-row-${e.employee_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{e.name}</div>
                        <div className="text-[11px] text-[#666666] truncate">
                          {e.department}
                          {Array.isArray(e.roles) && e.roles.length > 0 && (
                            <span> · {e.roles.join(", ")}</span>
                          )}
                        </div>
                      </div>
                      {canEditNominees && (
                        <button
                          type="button"
                          onClick={() => toggleNominee(e.employee_id)}
                          data-testid={`remove-nominee-${e.employee_id}`}
                          className="text-xs text-[#C41E1E] hover:underline inline-flex items-center gap-1 shrink-0"
                        >
                          <X size={12} /> Remove
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
              {canEdit && !conducted && !cancelled && (
                <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-end gap-3">
                  {nominationsConfirmed ? (
                    <button
                      type="button"
                      disabled
                      data-testid="nominations-confirmed-button"
                      className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 text-sm rounded-sm px-4 py-2 font-semibold cursor-default"
                    >
                      <Check size={14} /> Nominations Confirmed
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowConfirmNominations(true)}
                      disabled={confirmedNominees.length === 0}
                      data-testid="confirm-nominations-button"
                      className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check size={14} /> Confirm Nomination List
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
            {/* Suggested */}
            <div className="bc-card">
              <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
                <h3 className="font-heading font-semibold">Auto-suggested (open TNI gaps)</h3>
                <span className="text-xs text-[#666666]">{suggested.length}</span>
              </div>
              <div className="divide-y divide-[#E5E5E5]">
                {suggested.length === 0 && <div className="px-5 py-6 text-sm text-[#666666] text-center">No employees with open gaps for this skill.</div>}
                {suggested.map((s) => (
                  <div key={s.employee_id} className="px-5 py-2.5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{s.name}</div>
                      <div className="text-xs text-[#666666]">{s.department}</div>
                    </div>
                    {nomSet.has(s.employee_id) ? (
                      <span className="text-xs text-green-700 flex items-center gap-1"><Check size={12} /> Nominated</span>
                    ) : canEditNominees && (
                      <button data-testid={`nominate-${s.employee_id}`} onClick={() => toggleNominee(s.employee_id)} className="text-xs text-[#C41E1E] font-medium hover:underline">
                        + Nominate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* All employees */}
            <div className="bc-card">
              <div className="px-5 py-3 border-b border-[#E5E5E5]">
                <h3 className="font-heading font-semibold mb-2">All employees</h3>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or department…" className="w-full border border-[#E5E5E5] rounded-sm px-3 py-1.5 text-sm" data-testid="employee-search" />
              </div>
              <div className="divide-y divide-[#E5E5E5] max-h-[400px] overflow-y-auto">
                {filteredEmployees.map((s) => (
                  <div key={s.employee_id} className="px-5 py-2 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{s.name}</div>
                      <div className="text-[10px] text-[#666666]">{s.department} · {s.roles.join(", ")}</div>
                    </div>
                    {canEditNominees && (
                      <button onClick={() => toggleNominee(s.employee_id)} data-testid={`toggle-${s.employee_id}`} className={`text-xs px-2 py-1 border rounded-sm ${nomSet.has(s.employee_id) ? "bg-[#0D0D0D] text-white border-[#0D0D0D]" : "border-[#E5E5E5] hover:border-[#0D0D0D]"}`}>
                        {nomSet.has(s.employee_id) ? "Remove" : "Nominate"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        )}

        {tab === "conduct" && (canEdit || conducted) && !cancelled && (
          conducted && training ? (
            <TrainingRecordView training={training} empById={empById} canEdit={canEdit} onUpdated={() => load()} />
          ) : (
            <ConductForm calendar={entry} empById={empById} onDone={() => { load(); setTab("conduct"); }} canEdit={canEdit} />
          )
        )}
      </div>

      {showRevise && (
        <ReviseMonthModal
          entry={entry}
          onClose={() => setShowRevise(false)}
          onConfirm={(m) => reviseMonth(m)}
        />
      )}

      {confirmCancel && (
        <ConfirmCancelModal
          entry={entry}
          onCancel={() => setConfirmCancel(false)}
          onConfirm={cancelEntry}
        />
      )}

      {showConfirmNominations && (
        <ConfirmNominationsModal
          entry={entry}
          count={confirmedNominees.length}
          busy={confirmingNoms}
          onCancel={() => setShowConfirmNominations(false)}
          onConfirm={confirmNominations}
        />
      )}
    </div>
  );
}

function TrainingRecordView({ training, empById, canEdit, onUpdated }) {
  const [downloading, setDownloading] = useState(false);
  const [editing, setEditing] = useState(false);

  const exportPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/trainings/${training.training_id}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `TrainingRecord_${training.training_id}.pdf`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Training Record PDF downloaded");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bc-card" data-testid="training-record-view">
      <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start justify-between gap-4">
        <div>
          <div className="bc-label">Training Record · {training.training_id}</div>
          <h3 className="font-heading text-lg font-semibold mt-0.5">{training.skill_name}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="edit-attendance-button"
              className="inline-flex items-center gap-1.5 text-sm border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm px-3 py-2"
            >
              <Pencil size={14} /> Edit Attendance
            </button>
          )}
          <button
            type="button"
            onClick={exportPdf}
            disabled={downloading}
            data-testid="export-training-pdf-button"
            className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-neutral-800 text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {downloading ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-4 gap-4 px-5 py-4 text-sm border-b border-[#E5E5E5]">
        <div><div className="bc-label">Date</div><div className="font-mono mt-1">{training.date_conducted}</div></div>
        <div><div className="bc-label">Trainer</div><div className="mt-1">{training.trainer_name}</div></div>
        <div><div className="bc-label">Mode</div><div className="mt-1 capitalize">{training.mode}</div></div>
        <div><div className="bc-label">Duration</div><div className="font-mono mt-1">{training.duration_hours} hrs</div></div>
      </div>
      <div className="px-5 py-4 border-b border-[#E5E5E5]">
        <div className="bc-label mb-2">Attendance ({training.attendance.filter((a) => a.present).length}/{training.attendance.length} present)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {training.attendance.map((a) => (
            <div key={a.employee_id} className={`text-sm px-3 py-2 border rounded-sm ${a.present ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${a.present ? "bg-green-500" : "bg-red-500"}`} />
                <span className="font-medium">{empById[a.employee_id]?.name || `#${a.employee_id}`}</span>
              </div>
              <div className="text-[10px] text-[#666666] ml-4">{a.present ? "Present" : "Absent"}</div>
            </div>
          ))}
        </div>
      </div>
      {training.material_path && (
        <div className="px-5 py-3 text-sm flex items-center gap-2 border-b border-[#E5E5E5]">
          <FileText size={14} className="text-[#C41E1E]" /> Material:
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files?path=${encodeURIComponent(training.material_path)}`} target="_blank" rel="noreferrer" className="text-[#C41E1E] underline">Open</a>
        </div>
      )}
      {training.photo_paths.length > 0 && (
        <div className="px-5 py-3 border-b border-[#E5E5E5]">
          <div className="bc-label mb-2">Photos ({training.photo_paths.length})</div>
          <div className="flex flex-wrap gap-2">
            {training.photo_paths.map((p) => (
              <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}/api/files?path=${encodeURIComponent(p)}`} target="_blank" rel="noreferrer" className="block w-20 h-20 border border-[#E5E5E5] flex items-center justify-center text-[#666666]">
                <ImageIcon size={20} />
              </a>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(training.attendance_edits) && training.attendance_edits.length > 0 && (
        <div className="px-5 py-3" data-testid="attendance-audit-log">
          <div className="bc-label mb-2">Attendance edits ({training.attendance_edits.length})</div>
          <div className="space-y-2">
            {training.attendance_edits.map((edit) => (
              <div key={`${edit.edited_at}-${edit.edited_by}`} className="border-l-2 border-amber-400 pl-3 py-1 text-xs" data-testid={`attendance-edit-${edit.edited_at}`}>
                <div className="text-[#666666]">
                  Attendance edited on <span className="font-mono">{(edit.edited_at || "").slice(0, 16).replace("T", " ")}</span> by{" "}
                  <strong>{edit.edited_by_name || `#${edit.edited_by}`}</strong>
                </div>
                <div className="text-[#1A1A1A] mt-1 space-y-0.5">
                  {edit.changes.map((c) => {
                    const name = empById[c.employee_id]?.name || `#${c.employee_id}`;
                    return (
                      <div key={`${edit.edited_at}-${c.employee_id}`}>
                        · <strong>{name}</strong>: {c.from} → <strong>{c.to}</strong>
                        {c.assessment_created && <> · created effectiveness assessment <span className="font-mono">{c.assessment_created}</span></>}
                        {c.assessment_cancelled && <> · cancelled pending effectiveness <span className="font-mono">{c.assessment_cancelled}</span></>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {editing && (
        <EditAttendanceModal
          training={training}
          empById={empById}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setEditing(false); onUpdated && onUpdated(updated); }}
        />
      )}
    </div>
  );
}

function ConductForm({ calendar, empById, onDone, canEdit }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [trainer, setTrainer] = useState("");
  const [mode, setMode] = useState("classroom");
  const [duration, setDuration] = useState(2);
  const [present, setPresent] = useState(new Set(calendar.nominated_participants));
  const [materialPath, setMaterialPath] = useState(null);
  const [materialName, setMaterialName] = useState("");
  const [photoPaths, setPhotoPaths] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState(null);  // {existing} when 30-day duplicate detected

  const togglePresent = (id) => {
    const next = new Set(present);
    next.has(id) ? next.delete(id) : next.add(id);
    setPresent(next);
  };

  const uploadFile = async (file, kind) => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    const res = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data;
  };

  const handleMaterial = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = await uploadFile(f, "material");
    setMaterialPath(r.path);
    setMaterialName(r.filename);
  };
  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files);
    const uploaded = await Promise.all(files.map((f) => uploadFile(f, "photo")));
    setPhotoPaths([...photoPaths, ...uploaded.map((u) => u.path)]);
  };

  const buildPayload = (override) => ({
    calendar_id: calendar.calendar_id,
    date_conducted: date,
    trainer_name: trainer,
    mode,
    duration_hours: parseFloat(duration),
    attendance: calendar.nominated_participants.map((id) => ({ employee_id: id, present: present.has(id) })),
    material_path: materialPath,
    photo_paths: photoPaths,
    override_duplicate: !!override,
  });

  const submitPayload = async (override) => {
    try {
      await api.post("/trainings", buildPayload(override));
      setDuplicateWarning(null);
      onDone();
    } catch (e) {
      const data = e.response?.data?.detail;
      if (e.response?.status === 409 && data?.code === "DUPLICATE_TRAINING_SESSION") {
        setDuplicateWarning({ existing: data.existing });
      } else {
        setErr(typeof data === "string" ? data : "Failed");
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Frontend validation: training date cannot be in the future (IATF 16949 audit compliance)
    const today = new Date().toISOString().slice(0, 10);
    if (date > today) {
      setErr("Training date cannot be in the future. Only past or today's date is allowed.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await submitPayload(false);
    } finally {
      setBusy(false);
    }
  };

  const confirmDuplicateRecord = async () => {
    setBusy(true);
    try {
      await submitPayload(true);
    } finally {
      setBusy(false);
    }
  };

  if (calendar.nominated_participants.length === 0) {
    return <div className="bc-card p-10 text-sm text-[#666666] text-center">Nominate participants before recording training.</div>;
  }
  if (!canEdit) {
    return <div className="bc-card p-10 text-sm text-[#666666] text-center">Only HR can record training sessions.</div>;
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="conduct-form">
      <div className="bc-card p-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="bc-label block mb-1">Date conducted</label>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="training-date" />
        </div>
        <div>
          <label className="bc-label block mb-1">Trainer name</label>
          <input required value={trainer} onChange={(e) => setTrainer(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="trainer-name" />
        </div>
        <div>
          <label className="bc-label block mb-1">Mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="training-mode">
            <option value="classroom">Classroom</option>
            <option value="OJT">OJT</option>
            <option value="external">External</option>
          </select>
        </div>
        <div>
          <label className="bc-label block mb-1">Duration (hours)</label>
          <input type="number" min="0" step="0.5" required value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" />
        </div>
      </div>

      <div className="bc-card">
        <div className="px-5 py-3 border-b border-[#E5E5E5]">
          <div className="bc-label">Attendance ({present.size}/{calendar.nominated_participants.length})</div>
          <div className="text-xs text-[#666666] mt-1">Only nominated participants can be marked. Effectiveness tests are auto-created for present attendees.</div>
        </div>
        <div className="divide-y divide-[#E5E5E5]">
          {calendar.nominated_participants.map((id) => (
            <label key={id} className="px-5 py-2.5 flex items-center justify-between cursor-pointer bc-row-hover">
              <div>
                <div className="text-sm font-medium">{empById[id]?.name || `#${id}`}</div>
                <div className="text-xs text-[#666666]">{empById[id]?.department}</div>
              </div>
              <input type="checkbox" checked={present.has(id)} onChange={() => togglePresent(id)} data-testid={`attendance-${id}`} className="w-4 h-4 accent-[#C41E1E]" />
            </label>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="dropzone rounded-sm p-6 flex flex-col items-center justify-center gap-2 text-sm cursor-pointer" data-testid="material-upload">
          <Upload size={20} className="text-[#666666]" />
          <span className="font-medium">{materialName || "Upload training material"}</span>
          <span className="text-xs text-[#666666]">PDF / DOC / DOCX</span>
          <input type="file" className="hidden" onChange={handleMaterial} accept=".pdf,.doc,.docx" />
        </label>
        <label className="dropzone rounded-sm p-6 flex flex-col items-center justify-center gap-2 text-sm cursor-pointer" data-testid="photos-upload">
          <ImageIcon size={20} className="text-[#666666]" />
          <span className="font-medium">{photoPaths.length ? `${photoPaths.length} photo(s) uploaded` : "Upload training photos"}</span>
          <span className="text-xs text-[#666666]">JPG / PNG · Multiple</span>
          <input type="file" multiple className="hidden" onChange={handlePhotos} accept="image/*" />
        </label>
      </div>

      {err && <div className="text-sm text-[#C41E1E] bg-red-50 px-4 py-2 rounded-sm border border-red-200">{err}</div>}

      <button type="submit" disabled={busy} data-testid="submit-conduct" className="bg-[#C41E1E] hover:bg-[#A31818] text-white px-6 py-3 rounded-sm font-medium text-sm disabled:opacity-60">
        {busy ? "Recording…" : "Record training session"}
      </button>

      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={() => setDuplicateWarning(null)}>
          <div
            onClick={(ev) => ev.stopPropagation()}
            className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md"
            data-testid="training-duplicate-warning"
          >
            <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start gap-3">
              <div className="w-9 h-9 bg-amber-50 text-amber-700 rounded-sm flex items-center justify-center shrink-0">
                <AlertTriangle size={18} />
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Recent training already recorded</h3>
                <p className="text-sm text-[#1A1A1A] mt-2">
                  A <strong>{duplicateWarning.existing.skill_name}</strong> training was already recorded on{" "}
                  <strong>{duplicateWarning.existing.date_conducted}</strong> by{" "}
                  <strong>{duplicateWarning.existing.trainer_name || "—"}</strong>.
                </p>
                <p className="text-xs text-[#666666] mt-2">
                  Are you sure you want to record another session? The override will be stored in the training record&apos;s
                  audit trail.
                </p>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDuplicateWarning(null)}
                disabled={busy}
                className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDuplicateRecord}
                disabled={busy}
                data-testid="duplicate-record-anyway"
                className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-semibold disabled:opacity-60"
              >
                {busy ? "Recording…" : "Record Anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}


function ReviseMonthModal({ entry, onClose, onConfirm }) {
  const [month, setMonth] = useState(entry.target_month);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = parseInt(month);
    if (next === entry.target_month) {
      onClose();
      return;
    }
    setBusy(true);
    await onConfirm(next);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md p-6 space-y-4"
        data-testid="revise-month-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Revise target month</h3>
            <div className="text-xs text-[#666666] mt-0.5">{entry.skill_name} · currently <span className="font-mono">{MONTHS[entry.target_month - 1]} {entry.year}</span></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div>
          <label className="bc-label block mb-1">New target month</label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="revise-month-select"
            className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <div className="text-[10px] text-[#666666] mt-1">
            The calendar will mark this entry as <span className="font-mono">REVISED</span> and show
            &quot;Revised from {MONTHS[(entry.original_month || entry.target_month) - 1]}&quot;.
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E5E5E5]">
          <button type="button" onClick={onClose} className="text-sm text-[#666666] hover:text-[#0D0D0D] px-3 py-2">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="confirm-revise-month"
            className="bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2 font-medium disabled:opacity-60"
          >
            {busy ? "Saving…" : "Revise"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmCancelModal({ entry, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md p-6"
        data-testid="confirm-cancel-modal"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-red-50 text-[#C41E1E] flex items-center justify-center rounded-sm shrink-0">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold">Cancel this training entry?</h3>
            <p className="text-sm text-[#1A1A1A] mt-2">
              <strong>{entry.skill_name}</strong> — {MONTHS[entry.target_month - 1]} {entry.year}
            </p>
            <ul className="text-xs text-[#666666] mt-3 space-y-1 list-disc pl-4">
              <li>Stays visible in the calendar with a <span className="font-mono">CANCELLED</span> badge.</li>
              <li>Excluded from the Annual Training Plan PDF.</li>
              <li>No nominees will be auto-suggested for this entry.</li>
              <li>Recording a training session against it will be blocked.</li>
            </ul>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            data-testid="cancel-cancel"
            className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm"
          >
            Keep entry
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="confirm-cancel-entry"
            className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-medium"
          >
            Cancel entry
          </button>
        </div>
      </div>
    </div>
  );
}


function ConfirmNominationsModal({ entry, count, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-md"
        data-testid="confirm-nominations-modal"
      >
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-start gap-3">
          <div className="w-9 h-9 bg-[#C9A84C]/15 text-[#8a7124] flex items-center justify-center rounded-sm shrink-0">
            <Check size={18} />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold">Confirm the nomination list</h3>
            <p className="text-sm text-[#1A1A1A] mt-2">
              You are confirming <strong>{count}</strong> {count === 1 ? "employee" : "employees"} for{" "}
              <strong>{entry.skill_name}</strong> training in{" "}
              <strong>{MONTHS[entry.target_month - 1]} {entry.year}</strong>.
            </p>
            <p className="text-xs text-[#666666] mt-2">
              This list will be used as the attendance register when the training is conducted.
              You can still make changes before the training is recorded by clicking
              &quot;Edit nominations&quot;.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm disabled:opacity-60"
          >
            Not yet
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-nominations-confirm"
            className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {busy ? "Confirming…" : "Confirm nomination list"}
          </button>
        </div>
      </div>
    </div>
  );
}


function EditAttendanceModal({ training, empById, onClose, onSaved }) {
  const [present, setPresent] = useState(() => new Set(training.attendance.filter((a) => a.present).map((a) => a.employee_id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (id) => {
    const next = new Set(present);
    next.has(id) ? next.delete(id) : next.add(id);
    setPresent(next);
  };

  const originalPresent = new Set(training.attendance.filter((a) => a.present).map((a) => a.employee_id));
  const changes = training.attendance.map((a) => {
    const wasPresent = originalPresent.has(a.employee_id);
    const nowPresent = present.has(a.employee_id);
    if (wasPresent === nowPresent) return null;
    return { employee_id: a.employee_id, from: wasPresent ? "present" : "absent", to: nowPresent ? "present" : "absent" };
  }).filter(Boolean);

  const save = async (e) => {
    e.preventDefault();
    if (changes.length === 0) {
      onClose();
      return;
    }
    setBusy(true); setErr("");
    try {
      const attendance = training.attendance.map((a) => ({
        employee_id: a.employee_id,
        present: present.has(a.employee_id),
      }));
      const res = await api.patch(`/trainings/${training.training_id}/attendance`, { attendance });
      const made = res.data?.attendance_edits?.slice(-1)?.[0];
      const created = made?.created_assessments?.length || 0;
      const cancelled = made?.cancelled_assessments?.length || 0;
      toast.success(`Attendance updated · ${created} new effectiveness assessment${created !== 1 ? "s" : ""}, ${cancelled} cancelled`);
      onSaved(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-[#E5E5E5] rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="edit-attendance-modal"
      >
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <h3 className="font-heading text-base font-semibold">Edit attendance</h3>
            <div className="text-xs text-[#666666] mt-0.5">{training.skill_name} · {training.date_conducted}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 flex items-start gap-2" data-testid="edit-attendance-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Editing attendance after recording will affect effectiveness assessment assignments.
              Adding an employee will create a new effectiveness assessment for them. Removing an employee will cancel
              their pending effectiveness assessment if not yet taken. Completed assessments are never affected.
            </span>
          </div>

          <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]" data-testid="edit-attendance-list">
            {training.attendance.map((a) => {
              const wasPresent = originalPresent.has(a.employee_id);
              const nowPresent = present.has(a.employee_id);
              const changed = wasPresent !== nowPresent;
              return (
                <label
                  key={a.employee_id}
                  className={`px-3 py-2 flex items-center justify-between cursor-pointer ${changed ? "bg-amber-50" : ""}`}
                  data-testid={`edit-row-${a.employee_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{empById[a.employee_id]?.name || `#${a.employee_id}`}</div>
                    <div className="text-[10px] text-[#666666]">{empById[a.employee_id]?.department}</div>
                    {changed && (
                      <div className="text-[10px] text-amber-800 mt-0.5">
                        {wasPresent ? "Will cancel pending effectiveness assessment" : "Will create new effectiveness assessment"}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs ${nowPresent ? "text-green-700 font-semibold" : "text-[#666666]"}`}>
                      {nowPresent ? "Present" : "Absent"}
                    </span>
                    <input
                      type="checkbox"
                      checked={nowPresent}
                      onChange={() => toggle(a.employee_id)}
                      data-testid={`edit-toggle-${a.employee_id}`}
                      className="accent-[#C41E1E] w-4 h-4"
                    />
                  </div>
                </label>
              );
            })}
          </div>

          <div className="text-xs text-[#666666]">
            {changes.length === 0 ? "No changes yet." : `${changes.length} change${changes.length === 1 ? "" : "s"} pending.`}
          </div>

          {err && <div className="text-xs text-[#C41E1E] bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-[#E5E5E5] flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="text-sm px-4 py-2 border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm disabled:opacity-60">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || changes.length === 0}
            data-testid="save-attendance-edits"
            className="text-sm px-4 py-2 bg-[#C41E1E] hover:bg-[#A31818] text-white rounded-sm font-semibold disabled:opacity-40"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
